import React from "react";
import { FiBox, FiStar, FiZap, FiLayers, FiGlobe, FiCheck } from "react-icons/fi";
import { useLandingPageContext } from "pages/Context/LandinPageContext";
import { IPeriodicTime } from "core/enums/plans.enum";
import { PlanWithDiscount } from "..";
import IconPresent from "components/Icons/IconPresent";
import LField from "components/Inputs/LField";

interface PlanCardProps {
  plan: PlanWithDiscount;
  benefitOrder: string[];
  isRecommended?: boolean;
}

const PlanCard = ({ plan, benefitOrder, isRecommended = false }:PlanCardProps) => {
  const { selectedPlanContext, setSelectedPlanContext, quantityOnDemandContext, setQuantityOnDemandContext } = useLandingPageContext();
  const periodDetails = {
    [IPeriodicTime.FREE]: { label: "processo", months: 0 },
    [IPeriodicTime.ON_DEMAND]: { label: "processo", months: 0 },
    [IPeriodicTime.MONTHLY]: { label: "mês", months: 1 },
    [IPeriodicTime.BIMONTHLY]: { label: "bimestre", months: 2 },
    [IPeriodicTime.QUARTERLY]: { label: "trimestre", months: 3 },
    [IPeriodicTime.SEMESTERLY]: { label: "semestre", months: 6 },
    [IPeriodicTime.YEARLY]: { label: "ano", months: 12 },
    [IPeriodicTime.SPECIFIC]: { label: "período", months: 0 },
  }[plan.period.type];
  const equivalentMonthlyAmount = periodDetails.months > 1
    ? plan.allAmount / periodDetails.months
    : null;
  const maxInstallments = Math.max(1, Number(plan.period.installmentCount) || 1);
  const formatMoney = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const paymentTotal = plan.allAmount * (plan.period.type === IPeriodicTime.ON_DEMAND && plan.maxOnDemandQuantity !== 1 ? quantityOnDemandContext : 1);
  const paymentDescription = plan.period.type === IPeriodicTime.MONTHLY
    ? "Pagamento mensal"
    : maxInstallments === 1
      ? `À vista: ${formatMoney(paymentTotal)}`
      : `Até ${maxInstallments}× de ${formatMoney(paymentTotal / maxInstallments)}`;
  const benefits = [...(plan.benefit ?? [])].sort((a, b) => benefitOrder.indexOf(a) - benefitOrder.indexOf(b));
  const isPlanOnDemand= plan.period.type === IPeriodicTime.ON_DEMAND;
  const lockQuantity = isPlanOnDemand && plan.maxOnDemandQuantity === 1;

  if(plan.period.type === IPeriodicTime.ON_DEMAND) {
    plan = {...plan, allAmountCurrency: `${quantityOnDemandContext}x R$ ${(plan.allAmount*quantityOnDemandContext).toFixed(2).replace(".", ",")}`};
  }

  const iconMap: { [key: string]: any } = {
    [IPeriodicTime.FREE]: IconPresent,
    [IPeriodicTime.ON_DEMAND]: FiBox,
    [IPeriodicTime.MONTHLY]: FiStar,
    [IPeriodicTime.BIMONTHLY]: FiLayers,
    [IPeriodicTime.QUARTERLY]: FiZap,
    [IPeriodicTime.SEMESTERLY]: FiLayers,
    [IPeriodicTime.YEARLY]: FiGlobe,
  };

  const IconComponent = iconMap[plan.period.type];

  return (
    <div
      className={`plan-comparison-card border transition-all duration-300 bg-white text-gray-800 ${isRecommended ? "plan-card-recommended" : ""} ${
        selectedPlanContext?._id === plan?._id
          ? "plan-card-selected"
          : ""
      }`}
    >
      <div className="plan-card-contents">
        <div className="plan-card-heading">
          {isRecommended && <span className="plan-card-recommendation">Recomendado</span>}
          <div className="plan-card-identity">
          {IconComponent && (
            <div className="plan-card-icon">
              <IconComponent
                className={"text-2xl text-blue-600 hover:text-blue-400"}
              />
            </div>
          )}
          <h3
            className="plan-card-name"
          >
            {plan.name}
          </h3>
          </div>
          {plan.description && <p className="plan-card-description">{plan.description}</p>}
        </div>
        <div className="plan-card-price-details">
        <p className="plan-card-price font-semibold">{formatMoney(plan.allAmount)}</p>
        <p className="plan-card-period">por {periodDetails.label}</p>
        {equivalentMonthlyAmount !== null && (
          <p className="plan-card-equivalent font-new-order text-sm">Equivalente a <span className="font-medium">{formatMoney(equivalentMonthlyAmount)}/mês</span></p>
        )}
        {isPlanOnDemand && !lockQuantity && <p className="font-new-order font-normal text-sm">Total {plan.allAmountCurrency}</p>}
        {plan.allAmount > 0 && (
          <div className="plan-card-payment font-new-order text-sm">
            <p>{paymentDescription}</p>
          </div>
        )}
        {plan.discount !== "0" && (
          <div className="plan-card-savings font-new-order">
            <p className="plan-card-savings-percentage">{plan.discount}</p>
            {plan.savingsAmount !== undefined && plan.savingsAmount > 0 && (
              <p className="plan-card-savings-total">
                <span>Economize <strong>{formatMoney(plan.savingsAmount)}</strong> no {periodDetails.label}</span>
              </p>
            )}
          </div>
        )}
        </div>
      </div>

        <ul className="plan-card-benefits">
          {benefits.map((item, index) => {
            return (
              <li key={index} className="plan-card-benefit">
                <span className="plan-card-check" aria-hidden="true"><FiCheck /></span>
                <p className="m-0 text-start">{item}</p>
              </li>
            );
          })

          }
        </ul>
      <div className="plan-card-actions">
        <div className="plan-card-quantity">
        { isPlanOnDemand && !lockQuantity &&
          <div className="flex items-center justify-start gap-3">
            <button
              type="button"
              onClick={() => setQuantityOnDemandContext((prev) => Math.max(1, prev - 1))}
              className="px-3 rounded-full text-xl font-bold text-blue-600 bg-white hover:bg-gray-200"
            >
              −
            </button>
            <LField
              type="number"
              min={1}
              value={quantityOnDemandContext}
              onChange={(e) => {
                const value = Math.max(1, parseInt(e.target.value) || 1);
                setQuantityOnDemandContext(value);
              }}
              className="text-center rounded-md py-1 my-0"
              classNameInput="text-center  no-spinner"
            />
            <button
              type="button"
              onClick={() => setQuantityOnDemandContext((prev) => prev + 1)}
              className="px-3 py-1 rounded-full text-xl font-bold text-blue-600 bg-white hover:bg-gray-200"
            >
              +
            </button>
          </div>
        }
        </div>
        <button
          className="plan-card-select transition-all duration-300"
          aria-pressed={selectedPlanContext?._id === plan._id}
          onClick={() => {
            if (lockQuantity) setQuantityOnDemandContext(1);
            setSelectedPlanContext(plan);
            // BUG-050: rolar suave até o formulário e dar foco no primeiro campo.
            setTimeout(() => {
              const form = document.getElementById("register-form");
              if (form) {
                form.scrollIntoView({ behavior: "smooth", block: "start" });
                const firstInput = form.querySelector<HTMLInputElement>("input:not([type=hidden]), select, textarea");
                firstInput?.focus({ preventScroll: true });
              }
            }, 0);
          }}
        >
          {selectedPlanContext?._id === plan._id ? "Selecionado" : "Selecionar"}
        </button>
      </div>
    </div>
  );
};

export default PlanCard;

